<template>
  <v-dialog
    lazy
    persistent
    :width="$store.state.common.largeDialogWidth"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :value="show"
    @input="(val) => $emit('update:show', val)"
  >
    <v-card>
      <v-card-title class="pb-0">
        <v-layout
          row
          align-center
          justify-center
        >
          <h1 class="headline">
            {{ $t('storeAssociationMemberNumberImport') }}
          </h1>
          <v-btn
            class="ml-auto mr-0 my-0"
            flat
            icon
            :title="$t('close')"
            @click="abort()"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-layout>
      </v-card-title>
      <v-card-text>
        <div class="subheading">
          {{ $t('storeAssociationMemberNumberImportHint') }}
        </div>
        <!-- Excel File Import -->
        <v-layout justify-start>
          <!-- File Drag and Drop Area -->
          <div
            id="drag-and-drop-area"
            style="visibility: hidden; opacity: 0"
          >
            <div
              id="drag-and-drop-label"
              :style="{
                'font-size': $vuetify.breakpoint.xsOnly ? '24px' : '32px',
              }"
            >
              {{ $t('dropFileHereForImport') }}
            </div>
          </div>
          <input
            ref="fileInput"
            :key="fileInputRefCounter"
            type="file"
            accept=".xlsx, .xls, .csv, .ods"
            hidden
            @change="onExcelFileChosen($event.target.files)"
          >
          <v-btn
            flat
            color="msr"
            class="ml-0 mt-4 mb-0"
            :title="$t('importFromExcelFileFirstColumn')"
            @click="$refs.fileInput.click()"
          >
            <v-icon left>
              insert_drive_file
            </v-icon>
            {{ $t('importFromFile') }}
          </v-btn>
        </v-layout>
        <v-textarea
          v-model="associationMemberNumbersText"
          hide-details
          class="mb-2"
          :placeholder="$t('storeAssociationMemberNumberImportPlaceholder')"
          clearable
        />
        <v-layout
          align-baseline
          justify-space-between
        >
          <!-- Number of matching players -->
          <v-slide-y-transition>
            <div
              v-if="playersMatchingAssociationMemberNumbers.length"
              class="success--text"
              style="min-height: 25px; margin-top: 4px"
            >
              {{ playersMatchingAssociationMemberNumbers.length }}
              {{ $t('storeAssociationMemberNumbersPlayersMatched') }}
            </div>
          </v-slide-y-transition>
          <!-- Number and list of unknown member numbers -->
          <v-slide-y-transition>
            <div
              v-if="unknownAssociationMemberNumbers.length"
              class="warning--text ml-auto"
              style="min-height: 28px"
            >
              <v-layout align-center>
                {{ unknownAssociationMemberNumbers.length }} {{ $t('of') }}
                {{ associationMemberNumbers.length }}
                {{ $t('storeAssociationMemberNumbersNotFound') }}
                <v-btn
                  v-model="expandUnknownMemberNumbers"
                  icon
                  small
                  color="warning--text"
                  class="ml-1 mr-0 my-0"
                  @click="
                    expandUnknownMemberNumbers = !expandUnknownMemberNumbers
                  "
                >
                  <v-icon v-if="!expandUnknownMemberNumbers">
                    keyboard_arrow_down
                  </v-icon>
                  <v-icon v-if="expandUnknownMemberNumbers">
                    keyboard_arrow_up
                  </v-icon>
                </v-btn>
              </v-layout>
              <v-divider
                v-if="expandUnknownMemberNumbers"
                class="mb-2"
              />
              <div
                v-if="expandUnknownMemberNumbers"
                class="ml-3"
              >
                {{ unknownAssociationMemberNumbers.sort().join(', ') }}
              </div>
            </div>
          </v-slide-y-transition>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <btn-ok
          round
          :disabled="playersMatchingAssociationMemberNumbers.length === 0"
          :label="$t('storeAssociationMemberNumberImportConfirm')"
          label-with-icon
          @click="emitMatchingDecoders()"
        />
        <v-spacer />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {importXlsx} from '@/deferredImports';

import fullPageDragAndDrop from '@/mixins/fullPageDragAndDrop.js';

import BtnOk from '@/components/ButtonOk';

export default {
  components: {
    BtnOk,
  },
  mixins: [fullPageDragAndDrop],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    /* List of candidate Decoders to select. */
    decoders: {
      type: Array[Object],
      required: true,
    },
  },
  data() {
    return {
      xlsx: null,

      associationMemberNumbersText: '',
      expandUnknownMemberNumbers: false,
      fileInputRefCounter: 0,
    };
  },
  computed: {
    /**
     * The store membernumbers pulled from the list of Decoders.
     */
    storeAssociationMemberNumbers() {
      return [
        ...new Set(
          this.decoders.map((decoder) => decoder.store.association_membernumber)
        ),
      ];
    },
    /**
     * The store membernumbers parsed from user input.
     */
    associationMemberNumbers() {
      return (this.associationMemberNumbersText || '')
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
    },
    /**
     * The membernumbers from user input that don't match anything.
     */
    unknownAssociationMemberNumbers() {
      return this.associationMemberNumbers.filter((membernumber) => {
        return this.storeAssociationMemberNumbers.indexOf(membernumber) === -1;
      });
    },
    playersMatchingAssociationMemberNumbers() {
      return this.decoders.filter((decoder) => {
        return (
          this.associationMemberNumbers.indexOf(
            decoder.store.association_membernumber
          ) !== -1
        );
      });
    },
  },
  watch: {
    show: function (show) {
      if (show) {
        this.$nextTick(() => {
          this.installFullPageDragAndDropEventHandlers();
        });
      } else {
        this.uninstallFullPageDragAndDropEventHandlers();
      }

      if (!this.xlsx && show) {
        importXlsx().then((lib) => (this.xlsx = lib));
      }
    },
  },
  methods: {
    /**
     * Hook for fullPageDragAndDrop mixin.
     */
    onFilesChosen(files) {
      this.onExcelFileChosen(files);
    },
    onExcelFileChosen(files) {
      if (!files || !files.length) {
        return;
      }
      const file = files[0];
      if (!file) {
        return;
      }

      const vm = this;
      const reader = new FileReader();
      reader.onload = function (e) {
        console.log('[DEBUG] Reading file: ' + file.name);
        const workbook = vm.xlsx.read(e.target.result, {type: 'buffer'});
        vm.parseDateFromWorkbook(workbook);
      };
      reader.readAsArrayBuffer(file);
    },
    parseDateFromWorkbook(workbook) {
      this.reset();

      const firstSheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[firstSheetName];

      const lines = [];
      const rows = this.xlsx.utils.sheet_to_json(sheet, {header: 1});
      for (const row of rows) {
        if (row[0]) {
          let firstColumn = row[0];
          firstColumn = String(firstColumn).trim();
          if (firstColumn) {
            lines.push(firstColumn);
          }
        }
      }

      const text = lines.join('\n');
      this.associationMemberNumbersText = text;

      this.resetFileInput();
    },
    resetFileInput() {
      this.fileInputRefCounter += 1;
    },
    abort() {
      this.reset();
      this.closeDialog();
    },
    closeDialog() {
      this.$emit('update:show', false);
    },
    reset() {
      this.associationMemberNumbersText = '';
      this.expandUnknownMemberNumbers = false;
      this.resetFileInput();
    },
    emitMatchingDecoders() {
      this.$emit('matched', this.playersMatchingAssociationMemberNumbers);
      this.reset();
      this.closeDialog();
    },
  },
};
</script>

<style scoped src="@/mixins/fullPageDragAndDrop.scoped.css"></style>



// WEBPACK FOOTER //
// src/components/DecoderSelectionImportDialog.vue